// ========================================
// PINTEREST OAUTH SERVICE
// ========================================
import { db, auth } from '../../firebase';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { oauthConfig, generateOAuthState } from '../../config/oauth.config';

export interface PinterestAuthResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  error?: string;
}

export interface PinterestProfile {
  id: string;
  username: string;
  profileImage?: string;
  accountType?: string;
}

export interface PinterestBoard {
  id: string;
  name: string;
  description?: string;
  privacy?: string;
}

class PinterestOAuthService {
  private readonly config = oauthConfig.pinterest;
  
  /**
   * Step 1: Start OAuth flow - Redirect user to Pinterest authorization
   */
  startOAuthFlow(): void {
    const state = generateOAuthState();
    
    console.log('📌 Starting Pinterest OAuth flow...');
    console.log('  - Generated state:', state);
    
    // Store state in sessionStorage for verification in callback
    sessionStorage.setItem('pinterest_oauth_state', state);
    sessionStorage.setItem('pinterest_oauth_timestamp', Date.now().toString());
    
    // Build authorization URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.config.appId,
      redirect_uri: this.config.redirectUri,
      state: state,
      scope: this.config.scopes.join(',') // Pinterest uses comma-separated scopes
    });
    
    const authUrl = `${this.config.authUrl}?${params.toString()}`;
    
    console.log('📍 Redirect URI:', this.config.redirectUri);
    console.log('🚀 Redirecting to Pinterest...');
    
    // Redirect to Pinterest
    window.location.href = authUrl;
  }
  
  /**
   * Step 2: Handle OAuth callback and exchange code for access token
   */
  async handleCallback(code: string, state: string): Promise<PinterestAuthResponse> {
    try {
      // Verify state to prevent CSRF attacks
      const storedState = sessionStorage.getItem('pinterest_oauth_state');
      const timestamp = sessionStorage.getItem('pinterest_oauth_timestamp');
      
      console.log('🔐 State validation:');
      console.log('  - Received state:', state);
      console.log('  - Stored state:', storedState);
      console.log('  - Match:', storedState === state);
      
      if (!storedState) {
        console.warn('⚠️ WARNING: sessionStorage was cleared during OAuth redirect!');
      } else if (storedState !== state) {
        throw new Error('Invalid state parameter - mogelijk CSRF aanval');
      }
      
      // Check if state is not too old (max 10 minutes)
      if (timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age > 10 * 60 * 1000) {
          throw new Error('OAuth state verlopen - probeer opnieuw');
        }
      }
      
      // Clear state from storage
      sessionStorage.removeItem('pinterest_oauth_state');
      sessionStorage.removeItem('pinterest_oauth_timestamp');
      
      console.log('✅ State verified, exchanging code for token...');
      
      // Exchange authorization code for access token via Cloud Function
      const tokenResponse = await this.exchangeCodeForToken(code);
      
      if (!tokenResponse.success || !tokenResponse.accessToken) {
        throw new Error(tokenResponse.error || 'Failed to get access token');
      }
      
      console.log('✅ Access token received');
      
      // Update connection status
      await this.updateConnectionStatus(true);
      
      console.log('✅ Pinterest account successfully connected!');
      
      return tokenResponse;
      
    } catch (error) {
      console.error('❌ Pinterest OAuth error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Exchange authorization code for access token
   * SECURE: Uses Firebase Cloud Function to keep client_secret on server
   */
  private async exchangeCodeForToken(code: string): Promise<PinterestAuthResponse> {
    try {
      console.log('🔐 Exchanging code for token via Cloud Function...');
      
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const pinterestTokenExchangeFn = httpsCallable(functions, 'pinterestTokenExchange');
      
      const result = await pinterestTokenExchangeFn({
        code: code,
        redirectUri: this.config.redirectUri
      });
      
      const data = result.data as any;
      
      if (!data.success) {
        throw new Error(data.error || 'Token exchange failed');
      }
      
      console.log('✅ Token exchange successful via Cloud Function');
      console.log('✅ Pinterest profile fetched:', data.profile);
      
      return {
        success: true,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresIn: data.expiresIn
      };
      
    } catch (error) {
      console.error('❌ Token exchange error:', error);
      throw error;
    }
  }
  
  /**
   * Update connection status in Firestore
   */
  private async updateConnectionStatus(connected: boolean): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    const userId = auth.currentUser.uid;
    const now = new Date();
    
    try {
      const connectionRef = doc(db, 'socialConnections', userId);
      const connectionDoc = await getDoc(connectionRef);
      
      console.log('📝 Updating Pinterest connection status. Connected:', connected);
      
      if (connectionDoc.exists()) {
        const updatedFields: any = {
          'pinterest.connected': connected,
          'updatedAt': now
        };
        
        if (connected) {
          updatedFields['pinterest.connectedAt'] = now;
        } else {
          updatedFields['pinterest.disconnectedAt'] = now;
        }
        
        await updateDoc(connectionRef, updatedFields);
        console.log('✅ Connection status updated');
        
      } else {
        await setDoc(connectionRef, {
          uid: userId,
          linkedin: { connected: false },
          facebook: { connected: false },
          instagram: { connected: false },
          twitter: { connected: false },
          pinterest: {
            connected: connected,
            connectedAt: connected ? now : undefined
          },
          createdAt: now,
          updatedAt: now
        });
        console.log('✅ New connection document created');
      }
      
    } catch (error) {
      console.error('❌ Error updating connection status:', error);
      throw error;
    }
  }
  
  /**
   * Get user's Pinterest boards for pin creation
   */
  async getUserBoards(userId: string): Promise<PinterestBoard[]> {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const getBoardsFn = httpsCallable(functions, 'getPinterestBoards');
      
      const result = await getBoardsFn({});
      const data = result.data as { success: boolean; boards?: PinterestBoard[]; error?: string };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to get boards');
      }
      
      return data.boards || [];
      
    } catch (error) {
      console.error('Error getting Pinterest boards:', error);
      return [];
    }
  }
  
  /**
   * Post content to Pinterest (create a pin)
   * SECURE: Uses Cloud Function with backend-stored tokens
   */
  async postToPinterest(
    content: string,
    userId: string,
    imageUrl: string,
    boardId?: string,
    link?: string
  ): Promise<{ success: boolean; pinUrl?: string; error?: string }> {
    try {
      console.log('📌 Creating Pinterest pin...');
      
      if (!imageUrl) {
        throw new Error('Pinterest vereist een afbeelding voor pins');
      }
      
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const postToPinterestFn = httpsCallable(functions, 'postToPinterest');
      
      const result = await postToPinterestFn({
        title: content.substring(0, 100), // Pinterest title max 100 chars
        description: content,
        imageUrl: imageUrl,
        boardId: boardId, // Optional: post to specific board
        link: link || 'https://superkonnected.nl'
      });
      
      const data = result.data as {
        success: boolean;
        pinUrl?: string;
        pinId?: string;
        error?: string;
      };
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to create pin');
      }
      
      console.log('✅ Pin created successfully:', data.pinUrl);
      
      return {
        success: true,
        pinUrl: data.pinUrl
      };
      
    } catch (error) {
      console.error('❌ Pinterest posting error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Disconnect Pinterest account
   */
  async disconnect(userId: string): Promise<void> {
    console.log('🔌 Pinterest disconnect starting for userId:', userId);
    
    try {
      const connectionRef = doc(db, 'socialConnections', userId);
      
      await updateDoc(connectionRef, {
        'pinterest.connected': false,
        'pinterest.disconnectedAt': new Date(),
        'pinterest.accessToken': null,
        'pinterest.refreshToken': null
      });
      
      console.log('✅ Pinterest disconnected from Firestore');
      
    } catch (error) {
      console.error('❌ Error disconnecting Pinterest:', error);
      throw error;
    }
  }
  
  /**
   * Check if Pinterest is connected
   */
  async isConnected(userId: string): Promise<boolean> {
    try {
      const connectionRef = doc(db, 'socialConnections', userId);
      const connectionDoc = await getDoc(connectionRef);
      
      if (!connectionDoc.exists()) return false;
      
      const pinterestData = connectionDoc.data().pinterest;
      return pinterestData?.connected === true;
      
    } catch (error) {
      console.error('Error checking Pinterest connection:', error);
      return false;
    }
  }
}

export const pinterestOAuth = new PinterestOAuthService();
